import { Terminal } from 'xterm';
import 'xterm/css/xterm.css';
// puttyApp.js
// Correction écran noir et crash xterm.js : debug DOM et logs init

import React, { useState, useRef, useEffect } from 'react';
import { Paper, Typography, TextField, Button, Box, FormControl, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress, Alert } from '@mui/material';
import { backendUrl, API_KEYS } from '../config';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';

const DEFAULT_PORT = 22;
const DEFAULT_USER = '';
const DEFAULT_HOST = '';

function PuttyTerminal({ open, onClose }) {
  const xtermRef = useRef(null);
  const [xterm, setXterm] = useState(null);
  const wsRef = useRef(null);
  const messageBuffer = useRef([]);
  const resizeTimeout = useRef(null);
  const initTimeout = useRef(null);
  const mountedRef = useRef(false);
  const destroyedRef = useRef(false);
  const [connecting, setConnecting] = useState(false);
  const [form, setForm] = useState({ host: '', port: 22, user: '', password: '' });
  const [error, setError] = useState('');
  const [xtermStatus, setXtermStatus] = useState('');

  useEffect(() => {
    if (xtermRef.current && !xterm) {
      ////console.log('Initialisation xterm.js, ref =', xtermRef.current);
      const term = new Terminal({
        fontFamily: 'monospace',
        fontSize: 15,
        theme: { background: '#181818' },
        cursorBlink: true,
        cols: 80,
        rows: 24,
      });
      term.open(xtermRef.current);
      setXterm(term);
      setXtermStatus('Terminal initialisé');
      try {
        term.writeln('\x1b[32m[Terminal prêt]\x1b[0m\r\n');
      } catch (e) {}
      resizeTimeout.current = setTimeout(() => {
        if (destroyedRef.current || !mountedRef.current || !term || !xtermRef.current) return;
        try {
          if (xtermRef.current.offsetWidth > 0 && xtermRef.current.offsetHeight > 0 && term._core && term._core.viewport) {
            term.resize(80, 24);
            term.refresh(0, 23);
            setXtermStatus(s => s + ' | resize/refresh OK');
          } else {
            setXtermStatus(s => s + ' | resize/refresh SKIP');
          }
        } catch (e) { setXtermStatus('Erreur resize/refresh: ' + e.message); }
      }, 100);
      if (messageBuffer.current.length > 0) {
        for (const msg of messageBuffer.current) {
          try {
            if (msg.type === 'data') {
              term.write(msg.data || '\x1b[33m[Sortie SSH vide]\x1b[0m');
            } else if (msg.type === 'error') {
              term.writeln('\x1b[31m[Erreur SSH ou connexion refusée]\x1b[0m');
            } else if (msg.type === 'ready') {
              term.writeln('\x1b[32m[Connecté à SSH]\x1b[0m');
            } else if (typeof msg === 'string') {
              term.write(msg);
            } else {
              term.writeln('\x1b[33m[MSG inconnu reçu]\x1b[0m');
              term.writeln(JSON.stringify(msg));
            }
            setConnecting(false); // Arrêt spinner dès qu'un message arrive
          } catch (e) {}
        }
        messageBuffer.current = [];
      }
    }
  }, [xtermRef.current, xterm]);

  useEffect(() => {
    ////console.log('PuttyApp mounted, terminalRef =', xtermRef.current);
    mountedRef.current = true;
    destroyedRef.current = false;
    setXtermStatus('');
    return () => {
      mountedRef.current = false;
      destroyedRef.current = true;
      if (initTimeout.current) {
        clearTimeout(initTimeout.current);
        initTimeout.current = null;
      }
      if (resizeTimeout.current) {
        clearTimeout(resizeTimeout.current);
        resizeTimeout.current = null;
      }
      if (xterm) {
        try { xterm.dispose(); } catch (e) {}
        setXterm(null);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  const handleConnect = () => {
    setConnecting(true);
    setError('');
    if (xterm) try { xterm.clear(); } catch (e) {}
    const ws = new window.WebSocket(`ws://${window.location.hostname}:8000/ws/ssh`);
    wsRef.current = ws;
    ws.onopen = () => {
      // 1. Envoyer d'abord les credentials API
      ws.send(JSON.stringify({
        api_key: API_KEYS.key,
        api_secret: API_KEYS.secret
      }));
      // 2. Puis envoyer les paramètres SSH
      ws.send(JSON.stringify({
        action: 'connect',
        ...form
      }));
    };
    ws.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch { msg = event.data; }
      if (!xterm) {
        messageBuffer.current.push(msg);
        return;
      }
      try {
        if (msg.type === 'error') {
          setError(msg.message);
          ws.close();
          setConnecting(false);
          xterm.writeln('\x1b[31m[Erreur SSH ou connexion refusée]\x1b[0m');
        } else if (msg.type === 'ready') {
          setConnecting(false);
          setXtermStatus('Connexion SSH établie, attente du prompt...');
          xterm.writeln('\x1b[32m[Connecté à SSH]\x1b[0m');
        } else if (msg.type === 'data') {
          setConnecting(false);
          xterm.write(msg.data || '\x1b[33m[Sortie SSH vide]\x1b[0m');
        } else if (typeof msg === 'string') {
          setConnecting(false);
          xterm.write(msg);
        } else {
          setConnecting(false);
          xterm.writeln('\x1b[33m[MSG inconnu reçu]\x1b[0m');
          xterm.writeln(JSON.stringify(msg));
        }
      } catch (e) {}
    };
    ws.onclose = () => {
      try {
        if (xterm) xterm.writeln('\r\n\x1b[31m[Déconnecté]\x1b[0m');
      } catch (e) {}
      setConnecting(false);
    };
    ws.onerror = () => {
      setError('Erreur de connexion WebSocket');
      setConnecting(false);
      try {
        if (xterm) xterm.writeln('\x1b[31m[Erreur de connexion WebSocket]\x1b[0m');
      } catch (e) {}
    };
    if (xterm) {
      try {
        xterm.onData(data => {
          if (ws.readyState === 1) ws.send(JSON.stringify({ action: 'input', data }));
        });
      } catch (e) {}
    }
  };

  const handleClose = () => {
    if (wsRef.current) wsRef.current.close();
    if (xterm) try { xterm.clear(); } catch (e) {}
    setForm({ host: '', port: 22, user: '', password: '' });
    setError('');
    if (onClose) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Connexion SSH interactive</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField label="Hôte" value={form.host} onChange={e => setForm(f => ({ ...f, host: e.target.value }))} size="small" sx={{ flex: 1 }} />
          <TextField label="Port" type="number" value={form.port} onChange={e => setForm(f => ({ ...f, port: e.target.value }))} size="small" sx={{ width: 100 }} />
          <TextField label="Utilisateur" value={form.user} onChange={e => setForm(f => ({ ...f, user: e.target.value }))} size="small" sx={{ flex: 1 }} />
          <TextField label="Mot de passe" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} size="small" sx={{ flex: 1 }} />
          <Button variant="contained" color="success" onClick={handleConnect} disabled={connecting || !form.host || !form.user || !form.password}>
            Connexion
          </Button>
        </Box>
        {error && <Typography color="error.main" sx={{ mb: 1 }}>{error}</Typography>}
        {connecting && <Box sx={{ mb: 1 }}><CircularProgress size={24} /></Box>}
        {xtermStatus && <Alert severity={xtermStatus.startsWith('Erreur') ? 'error' : 'info'} sx={{ mb: 1 }}>{xtermStatus}</Alert>}
        <div
          id="terminal-container"
          ref={xtermRef}
          style={{
            width: '100%',
            height: '60vh',
          }}
          onClick={() => console.log('Terminal container clicked, ref:', xtermRef.current)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PuttyApp() {
  const [command, setCommand] = useState('');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [host, setHost] = useState(DEFAULT_HOST);
  const [port, setPort] = useState(DEFAULT_PORT);
  const [user, setUser] = useState(DEFAULT_USER);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showTerminal, setShowTerminal] = useState(true);

  const handleChange = (e) => setCommand(e.target.value);
  const handleHostChange = (e) => setHost(e.target.value);
  const handlePortChange = (e) => setPort(Number(e.target.value));
  const handleUserChange = (e) => setUser(e.target.value);

  const handleRun = async () => {
    setLoading(true);
    setError('');
    setOutput('');
    try {
      const res = await fetch(`${backendUrl}/api/ssh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command, host, port, user })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setOutput(data.output || '');
      setHistory(h => [{ command, host, port, user, date: new Date().toISOString() }, ...h.slice(0, 19)]);
    } catch (e) {
      setError('Erreur: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleHistoryClick = (item) => {
    setCommand(item.command);
    setHost(item.host);
    setPort(item.port);
    setUser(item.user);
    setShowHistory(false);
  };
  const handleClearHistory = () => setHistory([]);

  useEffect(() => {
    ////console.log('PuttyApp mounted, terminalRef =', null);
  }, []);

  return (
    <Paper elevation={6} sx={{ p: 3, maxWidth: 700, margin: 'auto', mt: 4 }}>
      <Typography variant="h6" gutterBottom>Terminal SSH (Putty+)</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl sx={{ minWidth: 120 }}>
          <TextField label="Hôte" value={host} onChange={handleHostChange} size="small" placeholder="ex: 192.168.1.10" />
        </FormControl>
        <FormControl sx={{ minWidth: 80 }}>
          <TextField label="Port" type="number" value={port} onChange={handlePortChange} size="small" />
        </FormControl>
        <FormControl sx={{ minWidth: 120 }}>
          <TextField label="Utilisateur" value={user} onChange={handleUserChange} size="small" placeholder="ex: root" />
        </FormControl>
        <IconButton color="primary" onClick={() => setShowHistory(true)} title="Historique des commandes"><HistoryIcon /></IconButton>
        <Button variant="outlined" color="success" onClick={() => setShowTerminal(true)} sx={{ ml: 2 }}>
          Terminal interactif
        </Button>
      </Box>
      <TextField
        label="Commande SSH"
        value={command}
        onChange={handleChange}
        fullWidth
        multiline
        minRows={1}
        maxRows={4}
        sx={{ mb: 2 }}
        placeholder="ls -la"
        autoFocus
      />
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button variant="contained" color="primary" onClick={handleRun} disabled={loading || !command.trim() || !host || !user}>
          Exécuter
        </Button>
        <Button variant="outlined" color="secondary" onClick={() => setCommand('')}>Effacer</Button>
      </Box>
      {loading && <Typography color="info.main">Exécution...</Typography>}
      {error && <Typography color="error.main">{error}</Typography>}
      {output && (
        <Box sx={{ mt: 2, p: 2, bgcolor: '#111', color: '#0f0', borderRadius: 1, fontFamily: 'monospace', whiteSpace: 'pre-wrap', minHeight: 80 }}>
          {output}
        </Box>
      )}
      <Dialog open={showHistory} onClose={() => setShowHistory(false)}>
        <DialogTitle>Historique des commandes</DialogTitle>
        <DialogContent dividers>
          {history.length === 0 && <Typography>Aucun historique</Typography>}
          {history.map((item, idx) => (
            <Box key={idx} sx={{ mb: 1, p: 1, borderBottom: '1px solid #eee', cursor: 'pointer' }} onClick={() => handleHistoryClick(item)}>
              <Typography variant="body2" color="primary">{item.command}</Typography>
              <Typography variant="caption" color="textSecondary">{item.user}@{item.host}:{item.port} ({new Date(item.date).toLocaleString()})</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<DeleteIcon />} onClick={handleClearHistory}>Effacer l'historique</Button>
          <Button onClick={() => setShowHistory(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
      {showTerminal && (
        <PuttyTerminal open={showTerminal} onClose={() => setShowTerminal(false)} />
      )}
    </Paper>
  );
}

export function PuttyPage() {
  return (
    <div style={{ minHeight: '100vh', background: '#181818', paddingTop: 40 }}>
      <PuttyApp />
    </div>
  );
}
